import React from 'react';

import { INoteworthySocialProofFields } from '../../../types/contentful';

import { Asset } from '../AtomicComponents/Asset';
import cx from 'classnames';

export function NoteworthySocialProof({
  header,
  backgroundColor,
  badges
}: INoteworthySocialProofFields) {
  const defaultBgColor = '#F8F7F3';
  const bgColor = backgroundColor || defaultBgColor;
  const bgStyle = bgColor.startsWith('#')
    ? { backgroundColor: bgColor }
    : undefined;

  const hasDescription =
    badges &&
    badges.some(
      (badge) =>
        badge?.fields?.descriptionHeader || badge?.fields?.descriptionSubcopy
    );

  return (
    <div
      id="noteworthySocialProof"
      className="-mt-20 w-full px-4 py-5 tablet:-mt-[120px] tablet:px-6 tablet:py-10"
      style={bgStyle}
      data-blockid="noteworthySocialProof"
      data-testid="noteworthySocialProofContainer"
    >
      <div className="mx-auto flex w-max max-w-full flex-col items-center">
        <h2
          className="mb-4 w-full text-center text-[22px] font-normal leading-[28px] tracking-[0] text-[#111212] tablet:mb-10 tablet:text-[36px] tablet:leading-[43.2px]"
          data-testid="socialProofHeader"
        >
          {header}
        </h2>

        {badges && badges.length > 0 && (
          <div
            className={cx(
              'w-full items-center desktop:flex-row desktop:flex-nowrap desktop:justify-center',
              {
                'grid grid-cols-2 justify-items-center gap-2 desktop:flex desktop:flex-nowrap desktop:justify-center desktop:gap-10':
                  !hasDescription,
                'flex flex-col items-stretch justify-center gap-4 tablet:mx-auto tablet:max-w-2xl tablet:justify-center tablet:gap-6 desktop:max-w-none desktop:flex-row desktop:flex-wrap desktop:items-center':
                  hasDescription
              }
            )}
          >
            {badges.map((badge, index) => {
              const badgeFields = badge?.fields;
              const badgeImage = badgeFields?.badgeImage;
              const descriptionHeader = badgeFields?.descriptionHeader;
              const descriptionSubcopy = badgeFields?.descriptionSubcopy;

              return (
                <div
                  key={badge?.sys?.id || index}
                  className={cx('flex flex-shrink-0 flex-row items-center', {
                    'justify-center desktop:gap-10 [&:last-child:nth-child(odd)]:col-span-2 [&:last-child:nth-child(odd)]:justify-self-center':
                      !hasDescription,
                    'justify-start gap-3 tablet:gap-6 desktop:gap-4':
                      hasDescription
                  })}
                >
                  {badgeImage && (
                    <div
                      className={cx(
                        'flex flex-shrink-0 items-center justify-center [&_div]:object-contain [&_img]:object-contain',
                        'h-[80px] w-[80px] [&_div]:h-[80px] [&_div]:w-[80px] [&_img]:h-[80px] [&_img]:w-[80px]',
                        'tablet:h-[140px] tablet:w-[140px] tablet:[&_div]:h-[140px] tablet:[&_div]:w-[140px] tablet:[&_img]:h-[140px] tablet:[&_img]:w-[140px]',
                        { 'desktop:mr-16': !hasDescription }
                      )}
                    >
                      <Asset
                        asset={badgeImage}
                        alt={badgeFields?.contentfulLabel}
                      />
                    </div>
                  )}
                  {hasDescription && (
                    <div className="ml-2 flex min-w-0 flex-1 flex-col tablet:ml-6 tablet:mt-7 tablet:flex-1 desktop:ml-4 desktop:w-[165px] desktop:flex-initial">
                      {descriptionHeader && (
                        <h3
                          className="typography-title-4 mb-0.5 text-[13px] font-medium leading-[16px] tracking-[0] text-[#111212] tablet:mb-3 tablet:text-[16px] tablet:leading-[19.2px]"
                          data-testid={`descriptionHeader-${index}`}
                        >
                          {descriptionHeader}
                        </h3>
                      )}
                      {descriptionSubcopy && (
                        <p
                          className="text-[12px] font-normal leading-[15px] tracking-[0] text-[#111212] tablet:text-[16px] tablet:leading-[19.2px]"
                          data-testid={`descriptionSubcopy-${index}`}
                        >
                          {descriptionSubcopy}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
